import apiService from './api';

export interface HistoricalAnalytics {
  period: string;
  startDate: Date;
  endDate: Date;
  summary: {
    totalSamplesReceived: number;
    totalSamplesCompleted: number;
    totalSamplesRejected: number;
    averageTurnaroundTime: number;
    efficiency: number;
  };
  dailyBreakdown: Array<{
    date: string;
    received: number;
    completed: number;
    rejected: number;
    inProgress: number;
    byLaboratory: {
      [key: string]: {
        received: number;
        completed: number;
        avgProcessingTime: number;
      };
    };
  }>;
  laboratoryMetrics: {
    [laboratory: string]: {
      totalProcessed: number;
      avgProcessingTime: number;
      efficiency: number;
      rejectionRate: number;
      topElements: Array<{ element: string; count: number }>;
    };
  };
  trends: {
    samplesPerDay: number[];
    turnaroundTimes: number[];
    efficiencyRate: number[];
  };
}

export interface LaboratoryPerformance {
  laboratory: string;
  period: string;
  metrics: {
    totalSamples: number;
    completedSamples: number;
    averageProcessingTime: number;
    efficiency: number;
    rejectionRate: number;
    urgentSamplesHandled: number;
    onTimeDeliveryRate: number;
  };
  timeSeriesData: Array<{
    date: string;
    samplesReceived: number;
    samplesCompleted: number;
    avgProcessingTime: number;
  }>;
  elementAnalysis: Array<{
    element: string;
    count: number;
    avgProcessingTime: number;
  }>;
  technicianPerformance: Array<{
    technicianId: string;
    technicianName: string;
    samplesProcessed: number;
    avgProcessingTime: number;
    accuracy: number;
  }>;
}

export interface DailyReport {
  date: Date;
  summary: {
    newSamplesReceived: number;
    samplesCompleted: number;
    samplesInProgress: number;
    samplesRejected: number;
    urgentSamples: number;
  };
  laboratoryActivity: Array<{
    laboratory: string;
    received: number;
    inPreparation: number;
    inAnalysis: number;
    completed: number;
    avgProcessingTime: number;
  }>;
  clientActivity: Array<{
    clientName: string;
    samplesSubmitted: number;
    samplesCompleted: number;
    status: string;
  }>;
  alerts: Array<{
    type: 'urgent' | 'overdue' | 'quality' | 'capacity';
    message: string;
    severity: 'low' | 'medium' | 'high';
    affectedSamples: number;
  }>;
  comparisonWithPrevious: {
    percentChangeReceived: number;
    percentChangeCompleted: number;
    percentChangeEfficiency: number;
  };
}

export interface TrendAnalysis {
  metric: string;
  period: string;
  data: Array<{
    date: string;
    value: number;
    label: string;
  }>;
  summary: {
    average: number;
    trend: 'up' | 'down' | 'stable';
    changePercent: number;
  };
}

class AnalyticsService {
  private baseUrl = '/analytics';

  /**
   * Get historical analytics data
   */
  async getHistoricalAnalytics(
    period: string = 'week',
    startDate?: string,
    endDate?: string,
    laboratory?: string
  ): Promise<HistoricalAnalytics> {
    const params = new URLSearchParams({ period });
    
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (laboratory) params.append('laboratory', laboratory);

    return apiService.get<HistoricalAnalytics>(`${this.baseUrl}/historical?${params.toString()}`);
  }

  /**
   * Get laboratory performance metrics
   */
  async getLaboratoryPerformance(
    period: string = 'month',
    laboratory?: string
  ): Promise<LaboratoryPerformance[]> {
    const params = new URLSearchParams({ period });
    if (laboratory) params.append('laboratory', laboratory);

    return apiService.get<LaboratoryPerformance[]>(`${this.baseUrl}/laboratory-performance?${params.toString()}`);
  }

  /**
   * Get daily report for a specific date
   */
  async getDailyReport(date?: string): Promise<DailyReport> {
    const params = date ? new URLSearchParams({ date }) : '';
    return apiService.get<DailyReport>(`${this.baseUrl}/daily-report?${params.toString()}`);
  }

  /**
   * Get trend analysis for a specific metric
   */
  async getTrends(
    metric: 'samples' | 'turnaround' | 'efficiency' | 'rejection',
    period: string = 'month'
  ): Promise<TrendAnalysis> {
    const params = new URLSearchParams({ metric, period });
    return apiService.get<TrendAnalysis>(`${this.baseUrl}/trends?${params.toString()}`);
  }

  /**
   * Get multiple analytics at once for dashboard
   */
  async getDashboardAnalytics(period: string = 'week'): Promise<{
    historical: HistoricalAnalytics;
    laboratoryPerformance: LaboratoryPerformance[];
    dailyReport: DailyReport;
    samplesTrend: TrendAnalysis;
    efficiencyTrend: TrendAnalysis;
  }> {
    try {
      const [historical, laboratoryPerformance, dailyReport, samplesTrend, efficiencyTrend] = await Promise.all([
        this.getHistoricalAnalytics(period),
        this.getLaboratoryPerformance(period),
        this.getDailyReport(),
        this.getTrends('samples', period),
        this.getTrends('efficiency', period),
      ]);

      return {
        historical,
        laboratoryPerformance,
        dailyReport,
        samplesTrend,
        efficiencyTrend,
      };
    } catch (error) {
      console.error('Error fetching dashboard analytics:', error);
      throw error;
    }
  }

  /**
   * Get analytics for a specific date range
   */
  async getDateRangeAnalytics(
    startDate: string,
    endDate: string,
    laboratory?: string
  ): Promise<HistoricalAnalytics> {
    return this.getHistoricalAnalytics('custom', startDate, endDate, laboratory);
  }

  /**
   * Get laboratory comparison data
   */
  async getLaboratoryComparison(period: string = 'month'): Promise<{
    laboratories: LaboratoryPerformance[];
    comparison: {
      efficiency: { [lab: string]: number };
      avgProcessingTime: { [lab: string]: number };
      throughput: { [lab: string]: number };
    };
  }> {
    const laboratories = await this.getLaboratoryPerformance(period);
    
    const comparison = {
      efficiency: {},
      avgProcessingTime: {},
      throughput: {},
    };

    laboratories.forEach(lab => {
      comparison.efficiency[lab.laboratory] = lab.metrics.efficiency;
      comparison.avgProcessingTime[lab.laboratory] = lab.metrics.averageProcessingTime;
      comparison.throughput[lab.laboratory] = lab.metrics.totalSamples;
    });

    return { laboratories, comparison };
  }

  /**
   * Get alerts and notifications
   */
  async getAlerts(): Promise<DailyReport['alerts']> {
    const dailyReport = await this.getDailyReport();
    return dailyReport.alerts;
  }

  /**
   * Export analytics data as CSV
   */
  async exportAnalytics(
    type: 'historical' | 'laboratory' | 'daily',
    period: string = 'month',
    laboratory?: string
  ): Promise<Blob> {
    const params = new URLSearchParams({ type, period });
    if (laboratory) params.append('laboratory', laboratory);

    const response = await fetch(`${apiService.getBaseUrl()}${this.baseUrl}/export?${params.toString()}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to export analytics data');
    }

    return response.blob();
  }
}

export const analyticsService = new AnalyticsService();
export default analyticsService;
